import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { AppState } from "../../RootReducer";
import { getWithdrawableBalance } from "../../selectors/payments-selectors";
import { getCurrencyDefinitions, getPreferredFiatExchange } from "../../selectors/settings-selectors";
import { Big } from "big.js";
import CurrencyFormatter from "../../utils/CurrencyFormatter";
import { AmountInput } from "../../core/ui/molecules/AmountInput";
import { AppSelect, AppSelectOption } from "../../core/ui/atoms/AppSelect";
import { PriorityType } from "../../http/Types";
import { t } from "../../i18n";
import { useEffect } from "react";
import { hasFiatExchangeAmount, toFiatAmount } from "../../currency/currency-utils";
import { CryptoFormData } from "./CryptoDetails";

const CryptoAmountSelector: React.FC = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CryptoFormData>();
  const withdraw = useSelector((state: AppState) => state.payments.withdraw);
  const balance = useSelector(getWithdrawableBalance);
  const currencyDefinitions = useSelector(getCurrencyDefinitions);
  const preferredExchangeCurrency = useSelector(getPreferredFiatExchange);
  const amount = watch("amount");
  const priority = watch("priority");
  const context = withdraw?.withdrawContext;
  const priorityOptions = context?.cryptoContext?.fees?.priority;
  const currency = withdraw?.activeCurrency;

  const getCurrency = () => {
    return currencyDefinitions?.find((c) => c.code === currency);
  };

  const getMaxWithdrawAmount = () => {
    const maxWithdraw = context?.maxWithdraw || 0;
    const balanceAmount = balance?.balance?.amount || 0;
    return Math.min(Number(maxWithdraw), Number(balanceAmount));
  };

  useEffect(() => {
    const cryptoContext = context?.cryptoContext;
    if (cryptoContext?.fees?.priority && !priority) {
      const defaultPriority = cryptoContext.fees.priority.medium !== undefined ? "medium" : (Object.keys(cryptoContext.fees.priority)[0] as PriorityType);
      setValue("priority", defaultPriority);
    }
  }, [context, priority, setValue]);

  const getFeeAndFinalAmount = (fee?: string, amountValue?: string) => {
    if (!fee || !amountValue) return { fee: "N/A", finalAmount: "N/A" };
    try {
      const feeBig = new Big(fee);
      const finalAmount = new Big(amountValue).minus(feeBig);
      return {
        fee: CurrencyFormatter.formatWithSymbol(getCurrency(), feeBig),
        finalAmount: CurrencyFormatter.formatWithSymbol(getCurrency(), finalAmount),
      };
    } catch (e) {
      return { fee: "N/A", finalAmount: "N/A" };
    }
  };

  return (
    <div className="amount-selector-container">
      <label>{t("withdrawPage.amountPhaseTitle")}</label>
      <AmountInput
        value={amount || ""}
        onChange={(value) => setValue("amount", value, { shouldValidate: true })}
        currencySymbol={getCurrency()?.prefixSymbol || getCurrency()?.postfixSymbol}
        presets={[getMaxWithdrawAmount()]}
        max={getMaxWithdrawAmount()}
        inputProps={{
          placeholder: t("cashier.amount"),
          inputMode: "decimal",
          ...register("amount", {
            required: t("withdraw.withdrawEmptyError"),
            validate: (value) => {
              try {
                if (!value) return t("withdraw.withdrawEmptyError");
                if (value.includes(",")) {
                  return t("withdraw.dotInsteadOfCommaError");
                }

                const amountBig = new Big(value);

                if (amountBig.lte(0)) {
                  return t("withdraw.withdrawAmountNegativeError");
                }

                const balanceAmount = balance?.balance?.amount;
                if (balanceAmount && amountBig.gt(balanceAmount)) {
                  return t("store.bundles.amountError");
                }

                if (context?.minWithdraw && amountBig.lt(context.minWithdraw)) {
                  return t("store.bundles.amountErrorMinWithdraw") + ": " + context.minWithdraw;
                }

                if (context?.maxWithdraw && amountBig.gt(context.maxWithdraw)) {
                  return t("store.bundles.amountErrorMaxWithdraw") + ": " + context.maxWithdraw;
                }

                if (value.includes(".")) {
                  const currencyDef = getCurrency();
                  const fractionalDigits = currencyDef?.fractionalDigits;
                  const decimalPart = value.split(".")[1];
                  if (decimalPart && fractionalDigits !== undefined && decimalPart.length > fractionalDigits) {
                    return t("withdraw.amountErrorMaxDecimals") + ": " + fractionalDigits;
                  }
                }

                const cryptoContext = context?.cryptoContext;
                if (cryptoContext?.fees?.priority && priority) {
                  const fee = cryptoContext.fees.priority[priority];
                  if (fee && new Big(fee).gte(amountBig)) {
                    return t("withdraw.fee-greater-than-amount");
                  }
                }

                return true;
              } catch (e) {
                return t("withdraw.withdrawAmountInvalidError");
              }
            },
          }),
        }}
      />

      {errors.amount && <span className="error-after-submit">{errors.amount.message}</span>}

      {amount && !errors.amount && currency && hasFiatExchangeAmount(currency, preferredExchangeCurrency, currencyDefinitions) && (
        <span className="exchange-amount">
          {t("cashier.approximately")} {toFiatAmount(currency, parseFloat(amount), preferredExchangeCurrency, currencyDefinitions)}
        </span>
      )}

      {priorityOptions && Object.keys(priorityOptions).length > 0 && (
        <div className="network-priority mt-3">
          <label>{t("withdraw.networkPriority")}</label>
          <AppSelect
            initialValue={priority || ""}
            onChange={(priorityKey) => setValue("priority", priorityKey as PriorityType)}
            renderActiveOption={(props) => (
              <>{t("withdraw." + props?.value, { fee: CurrencyFormatter.formatWithSymbol(getCurrency(), priorityOptions?.[props?.value as PriorityType]) })}</>
            )}
            className="priority-select"
          >
            {Object.keys(priorityOptions).map((priorityKey) => (
              <AppSelectOption
                key={priorityKey}
                value={priorityKey || ""}
                label={t(`payments.method.${priorityKey}.name`, undefined, priorityKey) || ""}
                method={priorityKey}
              >
                <>{t("withdraw." + priorityKey, { fee: CurrencyFormatter.formatWithSymbol(getCurrency(), priorityOptions?.[priorityKey as PriorityType]) })}</>
              </AppSelectOption>
            ))}
          </AppSelect>

          {priority && amount && !errors.amount && (
            <div className="fee-description mt-2">
              <span className="feedback">
                {t("withdraw.fee-description", getFeeAndFinalAmount(context?.cryptoContext?.fees?.priority?.[priority]?.toString(), amount))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CryptoAmountSelector;
